<script setup>
import { onMounted, computed } from "vue";
import { ref } from "vue";
import { NButton, NCard, NTag, NAvatar, NBadge } from "naive-ui";
import { getProviderProfileImage, getProviderPhoneNumbers } from "../services/provider";
import { useI18n } from 'vue-i18n';
import { dt } from '@/utils/i18n'

const props = defineProps({
  provider: { type: Object, required: true },
  showEditButton: { type: Boolean, default: false },
  showBookmarkButton: { type: Boolean, default: false },
  isBookmarked: { type: Boolean, default: false }
});

const emit = defineEmits(['edit', 'bookmark', 'unbookmark']);

const isPhoneVisible = ref(false);
const profileImageUrl = ref(null);
const providerPhones = ref([]);
const loadingPhones = ref(false);
const showAllSpecializations = ref(false);
const { t } = useI18n();

onMounted(async () => {
  // Load profile image
  try {
    const imageResult = await getProviderProfileImage(props.provider.id);
    if (imageResult.success && imageResult.image_url) {
      profileImageUrl.value = imageResult.image_url;
    }
  } catch (imageError) {
    console.error('Error loading profile image:', imageError);
    // Keep the fallback initials
  }
});

const getInitials = (name) => {
  if (!name) return "U";
  return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
};

const getCurrentLanguage = () => {
  return localStorage.getItem("preferredLanguage") || "english";
};

// Helper function to parse JSON arrays from backend
const parseJsonArray = (jsonValue) => {
  if (Array.isArray(jsonValue)) return jsonValue;
  if (typeof jsonValue === 'string') {
    try {
      const parsed = JSON.parse(jsonValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

async function loadPhoneNumbers() {
  if (loadingPhones.value) return;
  try {
    loadingPhones.value = true;
    if (providerPhones.value.length === 0) {
      const result = await getProviderPhoneNumbers(props.provider.id);
      providerPhones.value = Array.isArray(result) ? result : (result.phone_numbers || []);
    }
    isPhoneVisible.value = true;
  } catch (error) {
    console.error("Failed to load phone numbers", error);
  } finally {
    loadingPhones.value = false;
  }
}

const handleEdit = () => emit('edit', props.provider);
const toggleBookmark = () => {
  props.isBookmarked
    ? emit('unbookmark', props.provider.id)
    : emit('bookmark', props.provider.id);
};

const getStatusType = (status) => {
  return status === 'verified' ? 'success' : 'warning';
};

const getStatusText = (status) => {
  return status === 'verified' ? '✓ Verified' : '⏳ Unverified';
};

// Computed properties for parsed arrays
const specializations = computed(() => parseJsonArray(props.provider.specializations));
const spokenLanguages = computed(() => parseJsonArray(props.provider.spoken_languages));
</script>

<template>
  <NCard class="provider-card" hoverable>
    <template #header>
      <div class="header-content">
        <NAvatar :size="48" round class="avatar" :src="profileImageUrl">
          <template v-if="!profileImageUrl">
            {{ getInitials(provider.provider_name) }}
          </template>
        </NAvatar>

        <div class="provider-meta">
          <router-link 
            :to="{
              name: 'provider-detail',
              params: { id: provider.id },
              query: { language: getCurrentLanguage() },
            }" 
            class="provider-name"
          >
            {{ provider.provider_name || "Unknown Provider" }}
          </router-link>
          
          <div v-if="provider.provider_type" class="provider-type">
            {{ provider.provider_type }}
          </div>

          <NBadge 
            :type="getStatusType(provider.status)"
            :value="getStatusText(provider.status)"
            class="status-badge"
          />
        </div>
      </div>
    </template>

    <div class="provider-info">
      <!-- Location -->
      <div v-if="provider.city || provider.state" class="info-row">
        <span class="icon">📍</span>
        <span>{{ [provider.city, provider.state].filter(Boolean).join(', ') }}</span>
      </div>

      <!-- Specializations -->
      <div v-if="specializations.length" class="info-row">
        <span class="icon">⚖️</span>
        <strong>{{ dt('providers.specializations') || 'Specializations' }}:</strong>
        <div class="tags">
          <NTag
            v-for="(spec, index) in showAllSpecializations ? specializations : specializations.slice(0, 2)"
            :key="spec" 
            :type="['primary', 'info', 'success'][index % 3]" 
            size="small"
            class="capitalize"
          >
            {{ spec }}
          </NTag>
          <NTag 
            v-if="specializations.length > 2" 
            type="default" 
            size="small" 
            class="toggle-tag"
            @click="showAllSpecializations = !showAllSpecializations"
          >
            {{ showAllSpecializations ? 'Show Less' : `+${specializations.length - 2} more` }}
          </NTag>
        </div>
      </div>

      <!-- Spoken Languages -->
      <div v-if="spokenLanguages.length" class="info-row">
        <span class="icon">💬</span>
        <strong>{{ dt('providers.languages') || 'Languages' }}:</strong>
        <div class="tags">
          <NTag
            v-for="lang in spokenLanguages"
            :key="lang"
            type="default"
            size="small"
          >
            {{ lang }}
          </NTag>
        </div>
      </div>

      <!-- New Clients & Virtual Consultations -->
      <div v-if="provider.accepts_new_clients !== null || provider.virtual_consultations !== null" class="info-row">
        <span class="icon">👥</span>
        <div class="service-badges">
          <NTag 
            v-if="provider.accepts_new_clients" 
            type="success" 
            size="small"
            class="service-tag"
          >
            ✅ {{ dt('providers.acceptsNewClients') || 'Accepting New Clients' }}
          </NTag>
          <NTag 
            v-if="provider.virtual_consultations" 
            type="info" 
            size="small"
            class="service-tag"
          >
            💻 {{ dt('providers.virtualConsultations') || 'Virtual Consultations' }}
          </NTag>
        </div>
      </div>

      <!-- Phone Numbers -->
      <div class="info-row">
        <span class="icon">📞</span>
        <div v-if="!isPhoneVisible">
          <button class="phone-button" @click="loadPhoneNumbers" :disabled="loadingPhones">
            <span v-if="loadingPhones" class="loading-dots">
              Loading<span>.</span><span>.</span><span>.</span>
            </span>
            <span v-else>
              <strong>{{ dt('providers.showPhone') || 'Show Phone' }}</strong>
            </span>
          </button>
        </div>

        <div v-else class="phone-list">
          <a 
            v-for="(phone, index) in providerPhones" 
            :key="index" 
            :href="`tel:${phone}`" 
            class="phone-link"
          >
            {{ phone }}
          </a>
        </div>
      </div>

      <!-- Average Response Time -->
      <div v-if="provider.average_response_time_minutes" class="info-row">
        <span>{{ dt('providers.responseTime', { minutes: provider.average_response_time_minutes }) || `Avg. response: ${provider.average_response_time_minutes}h` }}</span>
      </div>
    </div>

    <template #action>
      <div class="provider-actions">
        <NButton 
          size="small" 
          type="primary" 
          class="action-button"
          @click="$router.push({ name: 'provider-detail', params: { id: provider.id } })"
        >
          👁️ {{ dt('providers.viewDetails') || 'View Details' }}
        </NButton>

        <NButton 
          v-if="showBookmarkButton" 
          size="small" 
          :type="isBookmarked ? 'warning' : 'default'"
          class="action-button" 
          @click="toggleBookmark"
        >
          {{ isBookmarked ? '⭐ Bookmarked' : '☆ Bookmark' }}
        </NButton>

        <NButton 
          v-if="showEditButton" 
          size="small" 
          type="info" 
          class="action-button" 
          @click="handleEdit"
        >
          ✏️ {{ dt('providers.edit') || 'Edit' }}
        </NButton>
      </div>
    </template>
  </NCard>
</template>

<style scoped>
.provider-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.action-button {
  min-width: 90px;
}

.service-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.service-tag {
  font-size: 0.75rem;
}

@media (max-width: 600px) {
  .provider-actions {
    gap: 6px;
  }

  .action-button {
    font-size: 0.8rem;
    padding: 6px 8px;
  }

  .service-badges {
    flex-direction: column;
    align-items: flex-start;
  }
}

.provider-card {
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 16px;
  transition: box-shadow 0.3s ease;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  font-weight: bold;
}

.provider-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.provider-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  text-decoration: none;
  transition: color 0.3s ease;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.provider-name:hover {
  color: #2563eb;
  text-decoration: underline;
}

.provider-type {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.status-badge {
  align-self: flex-start;
}

.provider-info {
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 0.875rem;
  flex-wrap: wrap;
}

.icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.capitalize {
  text-transform: capitalize;
}

.toggle-tag {
  cursor: pointer;
  background-color: #f3f4f6;
}

.phone-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.2s;
}

.phone-button:hover {
  background-color: #e5e7eb;
}

.phone-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-dots span {
  animation: dots 1.5s infinite;
  opacity: 0;
}

.loading-dots span:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.5s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 1s;
}

@keyframes dots {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

.phone-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.phone-link {
  color: #2563eb;
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s;
}

.phone-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}
</style>